<template>
    <div
        :class="[
            'speaker-select',
            { 'is-opened': opened, 'speaker-select--theme-light': theme === 'light' },
        ]"
        :style="{ width }"
    >
        <SmallPlayer
            v-if="showPrependPlayer"
            class="speaker-select__player speaker-select__player--prepend"
            :src="selectedOption.sample"
            :active="samplePlaying === selectedOption.sample"
            @playing="setSelectedPlayingSample"
        />
        <CustomSelect
            :model-value="speaker"
            :options="filteredOptions"
            :emit-prop="emitProp"
            :empty-text="emptyText"
            :with-filter="withFilter"
            :placeholder="placeholderComputed"
            :form-value-text="selectedSpeakerLabel"
            :filter-value-text="getSpeakerName"
            :width="width"
            :meta-tags="computedMetaTags"
            :disabled="isDisabled"
            :async="async"
            :can-load-more="canLoadMore"
            :multiple-filter="!async && multipleFilter"
            @opened="onOpened"
            @closed="onClosed"
            @update:model-value="onSelected"
            @filter="$emit('filter', $event)"
            @loadmore="$emit('loadmore')"
        >
            <template #list-prepend>
                <slot name="list-prepend" :close="onClosed" />
            </template>
            <template #list-append>
                <slot name="list-append" :close="onClosed" />
            </template>

            <template #default="{ item }">
                <SmallPlayer
                    v-if="item.sample"
                    class="speaker-select__player speaker-select__player--item"
                    :src="item.sample"
                    :active="samplePlaying === item.sample"
                    @playing="setPlayingSample"
                />
                {{ getSpeakerName(item) }}
                <div
                    v-if="item.is_recommended"
                    v-tooltip="$t('editor.toolbar.weRecommend')"
                    style="margin-left: 6px"
                >
                    <IconStar fill="#A6A7BE" />
                </div>
                <button
                    v-if="$isClientApp && item.provider_information"
                    class="button button--blank speaker-select__provider-icon"
                    type="button"
                    @click="showProviderModal(item)"
                >
                    <img
                        :src="item.provider_information.icon"
                        class="speaker-select__provider-icon-image"
                    />
                </button>
            </template>
            <template #help>
                <slot name="help"></slot>
            </template>
        </CustomSelect>
        <Modal v-if="selectedInfoSpeaker" class="provider-modal" @closed="closeProviderModal">
            <template #content="{ close }">
                <div class="provider-modal">
                    <a
                        :href="selectedInfoSpeaker.provider_information.link"
                        target="_blank"
                        class="provider-modal__logo"
                    >
                        <img
                            :src="selectedInfoSpeaker.provider_information.logo"
                            class="provider-modal__image"
                        />
                    </a>
                    <p
                        v-if="selectedInfoSpeaker.description"
                        v-html="selectedInfoSpeaker.description[$i18n.locale]"
                    ></p>
                </div>
            </template>
        </Modal>
    </div>
</template>

<script>
import i18n from '@speechki-frontend/core/i18n/i18n';
import { fetchSpeakerTags } from '@speechki-frontend/core/editor/api/speakers';
import { SmallPlayer } from '@speechki-frontend/shared/ui/Player';
import { CustomSelect } from '@speechki-frontend/shared/ui/forms';
import { IconStar } from '@speechki-frontend/shared/ui/icons';
import { Modal } from '@speechki-frontend/shared/ui/modals';

// @todo as moved to shared, should not have access to store, change to only use props

export default {
    name: 'SpeakerSelect',
    components: {
        CustomSelect,
        SmallPlayer,
        IconStar,
        Modal,
    },
    emits: ['update:modelValue', 'selected-option', 'opened', 'closed', 'filter', 'loadmore'],
    props: {
        modelValue: {},
        showOptions: {
            type: Boolean,
            default: true,
        },
        emitProp: {
            type: String,
            default: 'id',
        },
        emptyText: {
            type: String,
            default() {
                return i18n.global.t('editor.toolbar.noVoices');
            },
        },
        placeholder: {
            type: String,
            default() {
                return i18n.global.t('misc.select');
            },
        },
        withFilter: {
            type: Boolean,
            default: true,
        },
        multipleFilter: {
            type: Boolean,
            default: true,
        },
        speakers: {
            // null is to endicate taking speakers from store
            type: Array,
            default: null,
        },
        playerPrepend: {
            type: Boolean,
            default: false,
        },
        width: {
            type: [String, Number],
            default: 'auto',
        },
        igoneSpeakerTags: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        theme: {
            type: String,
            default: '',
        },
        async: {
            type: Boolean,
            default: false,
        },
        canLoadMore: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            speaker: null,
            selectedSpeakerCache: null,
            samplePlaying: null,
            opened: false,
            selectedInfoSpeaker: null,
            speakerTags: [],
        };
    },
    computed: {
        storeSpeakers() {
            const { settings: { speakers = [] } = {} } = this.$store.state;
            return speakers;
        },
        options() {
            if (!this.showOptions) return [];
            if (this.speakers && Array.isArray(this.speakers)) {
                return this.speakers;
            }

            return this.storeSpeakers;
        },
        filteredOptions() {
            return this.options;
        },
        computedMetaTags() {
            return this.speakerTags.map(({ name }) => name);
        },
        selectedOption() {
            return (
                this.options.find(({ id }) => id == this.speaker) || this.selectedSpeakerCache || {}
            );
        },
        selectedSpeakerLabel() {
            const found = this.options.find(({ id }) => id == this.speaker);
            if (found) return this.getSpeakerName(found);
            if (this.selectedSpeakerCache) return this.getSpeakerName(this.selectedSpeakerCache);
            return '';
        },
        showPrependPlayer() {
            return !this.opened && this.playerPrepend && this.selectedOption.sample;
        },
        placeholderComputed() {
            return this.multipleFilter ? this.placeholder : '...';
        },
        isDisabled() {
            if (this.disabled) return true;
            if (this.igoneSpeakerTags) return false;
            return !this.speakerTags.length;
        },
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(id) {
                this.speaker = id;
                if (!id) {
                    this.selectedSpeakerCache = null;
                    return;
                }
                const found = this.options.find(({ id: oid }) => oid == id);
                if (found) this.selectedSpeakerCache = found;
            },
        },
        options(opts) {
            if (!this.speaker) return;
            const found = opts.find(({ id }) => id == this.speaker);
            if (found) this.selectedSpeakerCache = found;
        },
    },
    async created() {
        if (!this.igoneSpeakerTags) {
            await this.fetchSpeakerTags();
        }
    },
    methods: {
        onOpened() {
            this.opened = true;
            this.stopPlayingSample();
            this.$emit('opened');
        },
        onClosed() {
            this.opened = false;
            this.stopPlayingSample();
            this.$emit('closed');
        },
        onSelected(event) {
            this.speaker = event;
            const found = this.options.find(({ id }) => id == event);
            if (found) this.selectedSpeakerCache = found;
            this.$emit('update:modelValue', event);
            // Full speaker object for consumers that need more than the id;
            // null when the selection was cleared or the id isn't in options.
            this.$emit('selected-option', found || null);
        },
        setPlayingSample({ src }) {
            this.samplePlaying = src;
        },
        stopPlayingSample() {
            this.samplePlaying = null;
        },
        setSelectedPlayingSample() {
            const { sample = null } = this.selectedOption;
            if (sample) {
                this.samplePlaying = sample;
            }
        },
        showProviderModal(speaker) {
            this.selectedInfoSpeaker = speaker;
        },
        closeProviderModal() {
            this.selectedInfoSpeaker = null;
        },
        getSpeakerName(speaker) {
            return (speaker.name && speaker.name[this.$i18n.locale]) || speaker.slug || '';
        },
        async fetchSpeakerTags() {
            const { results } = await fetchSpeakerTags();
            this.speakerTags = results;
        },
    },
};
</script>

<style lang="scss" src="./SpeakerSelect.scss"></style>
